// helpers/constants
import { EVENT_TYPE_PICKUP } from '../../constants/types';
// feature driver-cards-v2
import { TIMELINE_EVENT_KIND_DELIVERY } from './constants';
//////////////////////////////////////////////////

const DAY_MS = 24 * 60 * 60 * 1000;

const toEpoch = (value) => {
  const epoch = new Date(value).getTime();

  return Number.isFinite(epoch) ? epoch : null;
};

const sortIntervals = (left, right) => (
  left.startAt - right.startAt || left.endAt - right.endAt || left.id.localeCompare(right.id)
);

const getInterval = (load) => {
  const endAt = toEpoch(load.endAt);
  const startAt = toEpoch(load.startAt);

  if (!Number.isFinite(startAt) || !Number.isFinite(endAt) || startAt >= endAt) return null;

  return {
    load,
    endAt,
    startAt,
    id: String(load.id),
  };
};

const getOverlapComponents = (intervals) => intervals.reduce((components, interval) => {
  const current = components[components.length - 1];

  if (!current || interval.startAt >= current.endAt) {
    components.push({ endAt: interval.endAt, intervals: [interval] });

    return components;
  }

  current.endAt = Math.max(current.endAt, interval.endAt);
  current.intervals.push(interval);

  return components;
}, []);

export const clampVisibleDays = (value, minimum, maximum) => (
  Math.min(maximum, Math.max(minimum, value))
);

export const getTimelineViewportMetrics = ({
  hostWidth,
  railWidth,
  listViewportWidth,
  rightGutterWidth = 0,
}) => {
  const measuredListWidth = listViewportWidth > 0 ? listViewportWidth : hostWidth;
  const viewportWidth = Math.min(hostWidth, measuredListWidth);

  return {
    scrollbarWidth: Math.max(hostWidth - viewportWidth, 0),
    timelineViewportWidth: Math.max(viewportWidth - railWidth - rightGutterWidth, 1),
  };
};

export const getTimelineRange = ({ dateTo, dateFrom }) => {
  const startAt = toEpoch(dateFrom);
  const inclusiveEndAt = toEpoch(dateTo);

  return {
    startAt,
    endAt: Number.isFinite(inclusiveEndAt) ? inclusiveEndAt + 1 : null,
  };
};

export const getDateRangeDayCount = (dateRange) => {
  const { endAt, startAt } = getTimelineRange(dateRange);

  return Math.max(1, Math.round((endAt - startAt) / DAY_MS));
};

export const getVisibleTimeRange = ({
  endAt,
  startAt,
  scrollLeft,
  timelineWidth,
  viewportWidth,
}) => {
  const duration = endAt - startAt;
  const safeWidth = Math.max(timelineWidth, 1);
  const visibleStartAt = startAt + ((scrollLeft / safeWidth) * duration);
  const visibleEndAt = startAt + (((scrollLeft + viewportWidth) / safeWidth) * duration);

  return {
    endAt: Math.min(endAt, visibleEndAt),
    startAt: Math.max(startAt, visibleStartAt),
  };
};

export const getTimelineDays = (startAt, count) => Array.from(
  { length: count },
  (_, index) => new Date(startAt + (index * DAY_MS)),
);

export const buildNormalCascadeLayout = ({
  loads,
  endAt,
  startAt,
  visibleEndAt,
  timelineWidth,
  visibleStartAt,
  maximumVisibleDepth,
  timeOverscan = DAY_MS / 2,
}) => {
  const duration = endAt - startAt;

  if (!(duration > 0) || !(timelineWidth > 0)) return { cards: [], overflowBadges: [] };

  const intervals = loads
    .map(getInterval)
    .filter(Boolean)
    .filter(({ endAt: loadEnd, startAt: loadStart }) => (
      loadEnd > visibleStartAt - timeOverscan && loadStart < visibleEndAt + timeOverscan
    ))
    .sort(sortIntervals);

  const cards = [];
  const overflowBadges = [];
  const components = getOverlapComponents(intervals);

  components.forEach((component, cascadeIndex) => {
    let laneEndAts = [];
    let continuationIndex = 0;
    const hiddenByCascadeId = {};

    component.intervals.forEach((interval) => {
      let depth = laneEndAts.findIndex((laneEndAt) => laneEndAt <= interval.startAt);

      if (depth < 0 && laneEndAts.length < maximumVisibleDepth) depth = laneEndAts.length;

      if (depth < 0) {
        const visibleEnvelopeEnd = Math.max(...laneEndAts);
        const cascadeId = `cascade-${cascadeIndex}-${continuationIndex}`;

        if (interval.endAt <= visibleEnvelopeEnd) {
          const hidden = hiddenByCascadeId[cascadeId] || { endAt: -Infinity, loadIds: [] };

          hidden.endAt = Math.max(hidden.endAt, interval.endAt);
          hidden.loadIds.push(interval.id);
          hiddenByCascadeId[cascadeId] = hidden;

          return;
        }

        continuationIndex += 1;
        laneEndAts = [];
        depth = 0;
      }

      const clippedEnd = Math.min(endAt, interval.endAt);
      const clippedStart = Math.max(startAt, interval.startAt);

      laneEndAts[depth] = interval.endAt;
      cards.push({
        depth,
        id: interval.id,
        load: interval.load,
        clippedEnd: interval.endAt > endAt,
        clippedStart: interval.startAt < startAt,
        cascadeId: `cascade-${cascadeIndex}-${continuationIndex}`,
        left: ((clippedStart - startAt) / duration) * timelineWidth,
        width: ((clippedEnd - clippedStart) / duration) * timelineWidth,
      });
    });

    Object.entries(hiddenByCascadeId).forEach(([cascadeId, hidden]) => {
      const clippedEnd = Math.min(endAt, hidden.endAt);

      overflowBadges.push({
        cascadeId,
        hiddenLoadIds: hidden.loadIds,
        left: ((clippedEnd - startAt) / duration) * timelineWidth,
      });
    });
  });

  return { cards, overflowBadges };
};

const cloneEvents = (events = []) => events.map((event) => ({ ...event }));

export const cloneTimelineLoad = (load) => ({
  ...load,
  events: cloneEvents(load.events),
});

export const cloneTimelineLoads = (loads = []) => loads.map(cloneTimelineLoad);

const getEditableSignature = (loads) => JSON.stringify(loads.map((load) => ({
  id: load.id,
  rate: load.rate,
  endAt: toEpoch(load.endAt),
  startAt: toEpoch(load.startAt),
  draftChanges: load.draftChanges,
  events: (load.events || []).map(({ id, at }) => ({ id, at: toEpoch(at) })),
})));

export const areTimelineLoadsEqual = (left, right) => (
  getEditableSignature(left) === getEditableSignature(right)
);

export const applyTimelineCommand = (loads, command, direction) => {
  const snapshot = command[direction];
  const remainingLoads = loads.filter(({ id }) => id !== command.loadId);

  return snapshot
    ? [...remainingLoads, cloneTimelineLoad(snapshot)]
    : remainingLoads;
};

export const commitTimelineCommand = (session, command, historyLimit) => {
  const commandUnchanged = command.before && command.after
    ? areTimelineLoadsEqual([command.before], [command.after])
    : command.before === command.after;

  if (commandUnchanged) return session;

  const draftLoads = applyTimelineCommand(session.draftLoads, command, 'after');
  const undoStack = [...session.undoStack, command].slice(-historyLimit);

  return {
    ...session,
    undoStack,
    draftLoads,
    redoStack: [],
    dirty: !areTimelineLoadsEqual(session.baselineLoads, draftLoads),
  };
};

export const undoTimelineCommand = (session) => {
  if (!session.undoStack.length) return session;

  const command = session.undoStack[session.undoStack.length - 1];
  const draftLoads = applyTimelineCommand(session.draftLoads, command, 'before');

  return {
    ...session,
    draftLoads,
    undoStack: session.undoStack.slice(0, -1),
    redoStack: [...session.redoStack, command],
    dirty: !areTimelineLoadsEqual(session.baselineLoads, draftLoads),
  };
};

export const redoTimelineCommand = (session) => {
  if (!session.redoStack.length) return session;

  const command = session.redoStack[session.redoStack.length - 1];
  const draftLoads = applyTimelineCommand(session.draftLoads, command, 'after');

  return {
    ...session,
    draftLoads,
    redoStack: session.redoStack.slice(0, -1),
    undoStack: [...session.undoStack, command],
    dirty: !areTimelineLoadsEqual(session.baselineLoads, draftLoads),
  };
};

export const packEditLanes = (loads) => {
  const laneEnds = [];
  const loadsByLane = [];
  const laneByLoadId = {};
  const intervals = loads.map(getInterval).filter(Boolean).sort(sortIntervals);

  intervals.forEach((interval) => {
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= interval.startAt);

    if (lane < 0) lane = laneEnds.length;

    laneEnds[lane] = interval.endAt;
    laneByLoadId[interval.id] = lane;
    loadsByLane[lane] = [...(loadsByLane[lane] || []), interval.load];
  });

  return {
    loadsByLane,
    laneByLoadId,
    laneCount: Math.max(1, laneEnds.length),
  };
};

export const getEditRowHeight = (laneCount, laneHeight, verticalPadding, minimumHeight) => (
  Math.max(minimumHeight, verticalPadding + (laneCount * laneHeight))
);

export const projectIntervalToTimeline = ({ endAt, startAt }, rangeStartAt, rangeEndAt, width) => {
  const intervalEndAt = toEpoch(endAt);
  const intervalStartAt = toEpoch(startAt);
  const duration = rangeEndAt - rangeStartAt;
  const clippedEndAt = Math.min(rangeEndAt, intervalEndAt);
  const clippedStartAt = Math.max(rangeStartAt, intervalStartAt);

  return {
    left: ((clippedStartAt - rangeStartAt) / duration) * width,
    width: ((clippedEndAt - clippedStartAt) / duration) * width,
  };
};

export const snapTimelineTime = (value, snapMs) => Math.round(value / snapMs) * snapMs;

const clampTime = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const getEventEpochs = (load) => load.events.map(({ at }) => toEpoch(at));

export const getTimelineEventBounds = (load, eventIndex, range, minimumDuration) => {
  const endAt = toEpoch(load.endAt);
  const startAt = toEpoch(load.startAt);
  const eventAts = getEventEpochs(load);
  const firstPickupIndex = load.events.findIndex(({ kind }) => kind === EVENT_TYPE_PICKUP);

  const finalDeliveryIndex = load.events.reduce((result, event, index) => (
    event.kind === TIMELINE_EVENT_KIND_DELIVERY ? index : result
  ), -1);

  const previousEventAt = eventAts[eventIndex - 1] || startAt;
  const nextEventAt = eventAts[eventIndex + 1] || endAt;
  let minimum = Math.max(range.startAt, previousEventAt);
  let maximum = Math.min(range.endAt, nextEventAt);

  if (eventIndex === firstPickupIndex) {
    minimum = range.startAt;
    maximum = Math.min(endAt - minimumDuration, nextEventAt);
  }

  if (eventIndex === finalDeliveryIndex) {
    minimum = Math.max(startAt + minimumDuration, previousEventAt);
    maximum = range.endAt;
  }

  return {
    minimum,
    maximum,
    firstPickupIndex,
    finalDeliveryIndex,
  };
};

export const moveTimelineLoad = (load, deltaMs, rangeStartAt, rangeEndAt, snapMs) => {
  const endAt = toEpoch(load.endAt);
  const startAt = toEpoch(load.startAt);
  const snappedDelta = Math.round(deltaMs / snapMs) * snapMs;
  const clampedDelta = clampTime(snappedDelta, rangeStartAt - startAt, rangeEndAt - endAt);

  return {
    ...load,
    endAt: endAt + clampedDelta,
    startAt: startAt + clampedDelta,
    events: load.events.map((event) => ({
      ...event,
      at: toEpoch(event.at) + clampedDelta,
    })),
  };
};

export const resizeTimelineLoadStart = (load, nextStartAt, rangeStartAt, minimumDuration, snapMs) => {
  const endAt = toEpoch(load.endAt);
  const eventAts = getEventEpochs(load);
  const firstPickupIndex = load.events.findIndex(({ kind }) => kind === EVENT_TYPE_PICKUP);
  const nextEventAt = eventAts[firstPickupIndex + 1] || endAt;
  const maximum = Math.min(endAt - minimumDuration, nextEventAt);
  const startAt = clampTime(snapTimelineTime(nextStartAt, snapMs), rangeStartAt, maximum);

  return {
    ...load,
    startAt,
    events: load.events.map((event, index) => (
      index === firstPickupIndex ? { ...event, at: startAt } : event
    )),
  };
};

export const resizeTimelineLoadEnd = (load, nextEndAt, rangeEndAt, minimumDuration, snapMs) => {
  const startAt = toEpoch(load.startAt);
  const eventAts = getEventEpochs(load);

  const finalDeliveryIndex = load.events.reduce((result, event, index) => (
    event.kind === TIMELINE_EVENT_KIND_DELIVERY ? index : result
  ), -1);

  const previousEventAt = eventAts[finalDeliveryIndex - 1] || startAt;
  const minimum = Math.max(startAt + minimumDuration, previousEventAt);
  const endAt = clampTime(snapTimelineTime(nextEndAt, snapMs), minimum, rangeEndAt);

  return {
    ...load,
    endAt,
    events: load.events.map((event, index) => (
      index === finalDeliveryIndex ? { ...event, at: endAt } : event
    )),
  };
};

export const moveTimelineEvent = (load, eventIndex, nextAt, range, minimumDuration, snapMs) => {
  const {
    minimum,
    maximum,
    firstPickupIndex,
    finalDeliveryIndex,
  } = getTimelineEventBounds(load, eventIndex, range, minimumDuration);

  if (eventIndex === firstPickupIndex) {
    return resizeTimelineLoadStart(load, nextAt, range.startAt, minimumDuration, snapMs);
  }

  if (eventIndex === finalDeliveryIndex) {
    return resizeTimelineLoadEnd(load, nextAt, range.endAt, minimumDuration, snapMs);
  }

  const at = clampTime(snapTimelineTime(nextAt, snapMs), minimum, maximum);

  return {
    ...load,
    events: load.events.map((event, index) => (
      index === eventIndex ? { ...event, at } : event
    )),
  };
};
